import React from 'react';
import { isEmpty } from 'lodash';

import type { DisplaySpec } from '~/components/AiChat/types';
import {
  StructuredOutputKeyValue,
  type StructuredOutputRendererProps,
} from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/StructuredOutputKeyValue';
import { StructuredOutputSections } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/StructuredOutputSections';

// render_component → renderer. Adding a new structured-output layout is purely additive: write the
// renderer (reusing `FieldValue`) and add one entry here — the dispatcher contract and the
// per-field-type value logic stay untouched.
const RENDERERS: Partial<Record<string, React.FC<StructuredOutputRendererProps>>> = {
  key_value: StructuredOutputKeyValue,
  sections: StructuredOutputSections,
};

/**
 * Whether `display_spec` names a registered renderer. The card that frames the output asks this
 * before drawing its bar, so an unknown `render_component` shows nothing rather than a bar that
 * expands to nothing.
 */
export const hasStructuredOutputRenderer = (display_spec: DisplaySpec): boolean =>
  Boolean(RENDERERS[display_spec.render_component]);

interface StructuredOutputSectionProps {
  structured_output?: Record<string, unknown>;
  display_spec?: DisplaySpec;
}

/**
 * Thin dispatcher that renders an insight's structured output using the component named by
 * `display_spec.render_component`. Expert-agnostic — keyed only off the presence of
 * `structured_output` + `display_spec`. Renders nothing when either is missing/empty or the named
 * render component is not registered.
 */
export const StructuredOutputSection: React.FC<StructuredOutputSectionProps> = ({
  structured_output,
  display_spec,
}) => {
  if (!structured_output || isEmpty(structured_output) || !display_spec) {
    return null;
  }

  const Renderer = RENDERERS[display_spec.render_component];
  if (!Renderer) {
    return null;
  }

  return <Renderer structured_output={structured_output} display_spec={display_spec} />;
};
